import "../styleSheets/dashboardLegacyMessages.css";
import "../styleSheets/buttons.css";
import "../styleSheets/commonElements.css";
import React, { useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { convertUtcToLocal } from "../utilities/utilities";
import { useUser } from "../contexts/UserContext";
import { useLegacyMessages, useUserPlan } from "../graphql/hooks";
import WidgetState from "../components/WidgetState";
import { TooltipButton } from "../utilities/ToolTipButton";
import {
  type LegacyMessageListItem,
  isMessageEditableByStatus,
} from "../types/message";

function DashboardLegacyMessages() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { user, isAuthenticated } = useUser();
  const userId = user?.userId ?? "";

  const planQ = useUserPlan(userId, isAuthenticated && !!userId);
  const userPlan = planQ.data;
  // ✅ Call hooks unconditionally; use enabled to prevent fetch
  const {
    data: userMessages = [],
    isLoading,
    isError,
    error,
  } = useLegacyMessages(userId, isAuthenticated && !!userId);

  const {
    scheduledMessages,
    eolMessages,
    sendingSoonMessages,
    lastEditedMessage,
    sentMessages,
    totalMessages,
  } = useMemo(() => {
    if (!userMessages || userMessages.length === 0) {
      return {
        scheduledMessages: [] as LegacyMessageListItem[],
        eolMessages: [] as LegacyMessageListItem[],
        sendingSoonMessages: [] as LegacyMessageListItem[],
        lastEditedMessage: null as LegacyMessageListItem | null,
        sentMessages: [] as LegacyMessageListItem[],
        totalMessages: 0,
      };
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks

    const scheduled = userMessages.filter((msg) => !!msg.scheduledDate);
    const eol = userMessages.filter((msg) => msg.endOfLifeTrigger === true);

    const lastEdited = userMessages.reduce<LegacyMessageListItem | null>(
      (latest, msg) => {
        if (!msg.updatedAt) return latest;
        if (!latest?.updatedAt) return msg;
        return new Date(msg.updatedAt) > new Date(latest.updatedAt)
          ? msg
          : latest;
      },
      null,
    );

    const now = new Date();
    const in60Days = new Date(now);
    in60Days.setDate(now.getDate() + 60);

    const sendingSoon = userMessages
      .filter((msg) => {
        const notSent = !msg.sentDate || msg.sentDate.trim().length === 0;

        const hasRecipients =
          (msg.Recipients?.items?.filter((x) => x != null).length ?? 0) > 0;

        const hasTitle = !!msg.title?.trim();
        const hasBody = !!msg.bodySnippet?.trim();

        const hasScheduledDate = !!msg.scheduledDate?.trim();

        const scheduledInWindow =
          hasScheduledDate &&
          (() => {
            const d = new Date(msg.scheduledDate!);
            return d > now && d <= in60Days;
          })();

        const notEndOfLife = msg.endOfLifeTrigger !== true;

        return (
          notSent &&
          hasRecipients &&
          hasTitle &&
          hasBody &&
          hasScheduledDate &&
          scheduledInWindow &&
          notEndOfLife
        );
      })
      .sort(
        (a, b) =>
          new Date(a.scheduledDate!).getTime() -
          new Date(b.scheduledDate!).getTime(),
      )
      .slice(0, 2);

    const sent = userMessages.filter((msg) => !!msg.sentDate?.trim());

    return {
      scheduledMessages: scheduled,
      eolMessages: eol,
      sendingSoonMessages: sendingSoon,
      lastEditedMessage: lastEdited,
      sentMessages: sent,
      totalMessages: userMessages.length,
    };
  }, [userMessages]);

  const canCreateMessages =
    Number(userPlan?.messagesAllowed ?? 0) > totalMessages;

  function keepEditing(msg: LegacyMessageListItem | null) {
    if (msg && !isMessageEditableByStatus(msg.status)) {
      navigate(`/LegacyMessageView/${msg.id}`, { state: msg });
      return;
    }
    navigate(msg ? `/MessageEdit/${msg.id}` : "/MessageEdit/", { state: msg });
  }

  function addMessage() {
    navigate("/MessageEdit/", { state: null });
  }

  function showAllMessages() {
    navigate("/Panel?selectedTab=legacyMessages");
  }

  const hasAnyMessages = (userMessages?.length ?? 0) > 0;

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
        state={{ toast: { type: "info", message: t("common.signIn") } }}
      />
    );
  }

  return (
    <section className="section-legacy-messages" aria-labelledby="legacy-messages-heading">
      <div className="legacy-messages-title-container ">
        <div className="icon-container legacy-messages-icon" />
        <h2 id="legacy-messages-heading" className="legacy-messages-subtitle">
          {t("message.myLegacyMessages")}
        </h2>
      </div>

      {/* Top section: Last edited (loading/error/empty/content) */}
      <div className="last-message-section">
        <WidgetState
          isLoading={isLoading}
          isError={isError}
          error={error}
          isEmpty={!isLoading && !hasAnyMessages}
          emptyText={t("message.onScreenMessage.noMessages")}
          className="last-message-body"
        >
          <>
            <div className="last-edited-on">
              {`${t("common.lastEditedOn")} ${convertUtcToLocal(
                lastEditedMessage?.updatedAt,
                "ShowDayLong",
              )}`}
            </div>

            <div className="last-message-subject">
              {lastEditedMessage?.title}
            </div>
            <div className="last-message-body">
              {lastEditedMessage?.bodySnippet ?? ""}
            </div>

            <div className="keep-editing-button-container">
              <button
                className="btn btn--transparent"
                onClick={() => keepEditing(lastEditedMessage)}
                disabled={!lastEditedMessage}
              >
                <span className="legacy-section-button">
                  {t("message.keepEditing")}
                </span>
                <span className="right-arrow-icon red" />
              </button>
            </div>
          </>
        </WidgetState>
      </div>

      <div className="messages-legacy-divider" />

      {/* Middle: Sending soon */}
      <div className="sending-soon-section">
        <div className="sending-soon-title">
          {t("message.onScreenMessage.sendingSoon60D")}
        </div>

        {isLoading ? (
          <div className="sending-soon-empty">{t("common.loading")}</div>
        ) : isError ? (
          <div className="sending-soon-empty" style={{ color: "#ef4130" }}>
            {t("errorMessage.failedLoad")}
          </div>
        ) : !hasAnyMessages ? (
          <div className="sending-soon-empty">
            {t("message.onScreenMessage.createFirst")}
          </div>
        ) : sendingSoonMessages.length === 0 ? (
          <div className="sending-soon-empty">
            {t("message.onScreenMessage.noMessages60D")}
          </div>
        ) : (
          <div className="sending-soon-messages">
            {sendingSoonMessages.slice(0, 2).map((msg, idx) => (
              <React.Fragment key={msg?.id ?? idx}>
                {idx === 1 && <div className="sending-soon-vertical-divider" />}

                <div className="sending-soon-message-container">
                  <div className="sending-soon-message">
                    <div className="sending-soon-date">
                      {convertUtcToLocal(msg?.scheduledDate, "ShowDayShort")}
                    </div>
                    <div className="sending-soon-subject">{msg?.title}</div>
                  </div>

                  <div className="sending-soon-button-container">
                    <button
                      className="btn btn--transparent"
                      onClick={() => keepEditing(msg)}
                    >
                      <span className="legacy-section-button">
                        {t("message.openMessage")}
                      </span>
                      <span className="right-arrow-icon red" />
                    </button>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="messages-legacy-divider" />

      {/* Bottom: counts + actions */}
      <div className="actions-section">
        <div className="messages-count-container">
          <div className="message-count">{scheduledMessages.length}</div>
          <div className="message-title">{t("common.scheduled")}</div>
        </div>
        <div className="messages-count-container">
          <div className="message-count">{eolMessages.length}</div>
          <div className="message-title">{t("common.endOfLife")}</div>
        </div>
        <div className="messages-count-container">
          <div className="message-count">{sentMessages.length}</div>
          <div className="message-title">{t("common.sent")}</div>
        </div>

        <section className="button-medium-parent">
          <TooltipButton
            label={"+ " + t("message.newMessage")}
            tooltip={t("message.errorMessage.planLimitReached")}
            disabled={!canCreateMessages}
            onClick={addMessage}
            buttonClassName="btn btn--orange btn--size-md"
          />
          {hasAnyMessages && (
            <button
              className="btn btn--white btn--size-md"
              onClick={showAllMessages}
            >
              {t("common.seeAll")}
            </button>
          )}
        </section>
      </div>
    </section>
  );
}

export default DashboardLegacyMessages;
